import { useCallback, useEffect, useState } from "react"
import { API_BASE } from "../../../config/appConfig"
import { apiFetch } from "../../../utils/apiFetch"
import {
  UI_FEATURE_LOCK_TEMPLATE,
  BACKEND_FEATURE_TO_UI_FEATURE_KEY,
  normalizeFeatureErrorPayload
} from "../utils/dashboardFeatureLocks"

async function safeFetchResult(url, fallback) {
  try {
    const res = await apiFetch(url)
    const data = await res.json().catch(() => null)

    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        data: fallback,
        error: normalizeFeatureErrorPayload(data) || data || null
      }
    }

    return {
      ok: true,
      status: res.status,
      data: data ?? fallback,
      error: null
    }
  } catch (error) {
    return {
      ok: false,
      status: 0,
      data: fallback,
      error: error || null
    }
  }
}

export default function useDashboardDataLoader({
  clinicId,
  date,
  userId,
  plan,
  canAdvancedReports,
  canMultiClinic,
  canExecutive
}) {
  const [loading, setLoading] = useState(false)

  const [bundle, setBundle] = useState(null)
  const [heatmap, setHeatmap] = useState([])
  const [saturation, setSaturation] = useState([])
  const [doctorRanking, setDoctorRanking] = useState([])
  const [treatmentRanking, setTreatmentRanking] = useState([])
  const [weeklyForecast, setWeeklyForecast] = useState(null)
  const [monthlyForecast, setMonthlyForecast] = useState(null)
  const [financialAlerts, setFinancialAlerts] = useState([])
  const [executiveActions, setExecutiveActions] = useState([])
  const [executiveScore, setExecutiveScore] = useState([])
  const [featureLocks, setFeatureLocks] = useState(UI_FEATURE_LOCK_TEMPLATE)

  const clearFeatureLocks = useCallback((features = []) => {
    if (!Array.isArray(features) || !features.length) return

    setFeatureLocks((prev) => {
      let changed = false
      const next = { ...prev }

      features.forEach((feature) => {
        if (next[feature]) {
          next[feature] = null
          changed = true
        }
      })

      return changed ? next : prev
    })
  }, [])

  const registerFeatureLock = useCallback((rawError) => {
    const payload = normalizeFeatureErrorPayload(rawError)
    if (!payload) return false

    setFeatureLocks((prev) => {
      const next = { ...prev }

      payload.missingFeatures.forEach((backendFeature) => {
        const uiFeature = BACKEND_FEATURE_TO_UI_FEATURE_KEY[backendFeature]
        if (!uiFeature) return

        next[uiFeature] = {
          feature: uiFeature,
          backendFeature,
          message: payload.message,
          recommendedPlan: Array.isArray(payload.recommendedPlans)
            ? payload.recommendedPlans.find((item) => item?.feature === backendFeature)?.recommendedPlan || null
            : null,
          currentPlan: payload.currentPlan || null,
          clinicId: payload.clinicId || null
        }
      })

      return next
    })

    return true
  }, [])

  const canLoadSingleClinicAdvancedReports = useCallback(() => {
    if (!clinicId || clinicId === "ALL") return false
    if (!plan || !canAdvancedReports) return false

    return true
  }, [clinicId, plan, canAdvancedReports])

  const canLoadGlobalExecutiveFeeds = useCallback(() => {
    if (!clinicId || clinicId !== "ALL") return false
    if (!userId) return false
    if (!plan || !canMultiClinic || !canExecutive) return false

    return true
  }, [clinicId, userId, plan, canMultiClinic, canExecutive])

  const loadBundle = useCallback(async () => {
    if (!clinicId || !userId) {
      setBundle(null)
      return
    }

    const params = new URLSearchParams({
      userId: String(userId),
      date
    })

    if (clinicId && clinicId !== "ALL") {
      params.append("clinicId", clinicId)
    }

    const result = await safeFetchResult(
      `${API_BASE}/multi-clinic/dashboard-bundle?${params.toString()}`,
      null
    )

    setBundle(result.ok ? result.data || null : null)
  }, [clinicId, date, userId])

  const loadHeatmap = useCallback(async () => {
    if (!canLoadSingleClinicAdvancedReports()) {
      setHeatmap([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/appointments/heatmap-relative?clinicId=${clinicId}&date=${date}`,
      { hours: [] }
    )

    if (result.ok) {
      clearFeatureLocks(["advancedReports"])
      setHeatmap(Array.isArray(result.data?.hours) ? result.data.hours : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setHeatmap([])
      return
    }

    setHeatmap([])
  }, [
    clinicId,
    date,
    canLoadSingleClinicAdvancedReports,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadSaturation = useCallback(async () => {
    if (!canLoadSingleClinicAdvancedReports()) {
      setSaturation([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/appointments/saturation?clinicId=${clinicId}&date=${date}`,
      []
    )

    if (result.ok) {
      clearFeatureLocks(["advancedReports"])
      setSaturation(Array.isArray(result.data) ? result.data : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setSaturation([])
      return
    }

    setSaturation([])
  }, [
    clinicId,
    date,
    canLoadSingleClinicAdvancedReports,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadFinancialAlerts = useCallback(async () => {
    if (!canLoadGlobalExecutiveFeeds()) {
      setFinancialAlerts([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/multi-clinic/global-financial-alerts?userId=${encodeURIComponent(String(userId))}&dateFrom=${date}&dateTo=${date}`,
      []
    )

    if (result.ok) {
      clearFeatureLocks(["multiClinic", "executiveDashboard"])
      setFinancialAlerts(Array.isArray(result.data) ? result.data : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setFinancialAlerts([])
      return
    }

    setFinancialAlerts([])
  }, [
    date,
    userId,
    canLoadGlobalExecutiveFeeds,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadExecutiveActions = useCallback(async () => {
    if (!canLoadGlobalExecutiveFeeds()) {
      setExecutiveActions([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/multi-clinic/executive-actions?userId=${encodeURIComponent(String(userId))}&date=${date}`,
      []
    )

    if (result.ok) {
      clearFeatureLocks(["multiClinic", "executiveDashboard"])
      setExecutiveActions(Array.isArray(result.data) ? result.data : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setExecutiveActions([])
      return
    }

    setExecutiveActions([])
  }, [
    date,
    userId,
    canLoadGlobalExecutiveFeeds,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadExecutiveScore = useCallback(async () => {
    if (!canLoadGlobalExecutiveFeeds()) {
      setExecutiveScore([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/multi-clinic/executive-score?userId=${encodeURIComponent(String(userId))}&date=${date}`,
      []
    )

    if (result.ok) {
      clearFeatureLocks(["multiClinic", "executiveDashboard"])
      setExecutiveScore(Array.isArray(result.data) ? result.data : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setExecutiveScore([])
      return
    }

    setExecutiveScore([])
  }, [
    date,
    userId,
    canLoadGlobalExecutiveFeeds,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadDoctorRanking = useCallback(async () => {
    if (!canLoadSingleClinicAdvancedReports()) {
      setDoctorRanking([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/appointments/doctor-ranking?clinicId=${clinicId}&startDate=${date}`,
      []
    )

    if (result.ok) {
      clearFeatureLocks(["advancedReports"])
      setDoctorRanking(Array.isArray(result.data) ? result.data : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setDoctorRanking([])
      return
    }

    setDoctorRanking([])
  }, [
    clinicId,
    date,
    canLoadSingleClinicAdvancedReports,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadTreatmentRanking = useCallback(async () => {
    if (!canLoadSingleClinicAdvancedReports()) {
      setTreatmentRanking([])
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/appointments/treatment-ranking?clinicId=${clinicId}&startDate=${date}`,
      []
    )

    if (result.ok) {
      clearFeatureLocks(["advancedReports"])
      setTreatmentRanking(Array.isArray(result.data) ? result.data : [])
      return
    }

    if (registerFeatureLock(result.error)) {
      setTreatmentRanking([])
      return
    }

    setTreatmentRanking([])
  }, [
    clinicId,
    date,
    canLoadSingleClinicAdvancedReports,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadWeeklyForecast = useCallback(async () => {
    if (!canLoadSingleClinicAdvancedReports()) {
      setWeeklyForecast(null)
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/appointments/weekly-forecast?clinicId=${clinicId}&date=${date}`,
      null
    )

    if (result.ok) {
      clearFeatureLocks(["advancedReports"])
      setWeeklyForecast(result.data || null)
      return
    }

    if (registerFeatureLock(result.error)) {
      setWeeklyForecast(null)
      return
    }

    setWeeklyForecast(null)
  }, [
    clinicId,
    date,
    canLoadSingleClinicAdvancedReports,
    clearFeatureLocks,
    registerFeatureLock
  ])

  const loadMonthlyForecast = useCallback(async () => {
    if (!canLoadSingleClinicAdvancedReports()) {
      setMonthlyForecast(null)
      return
    }

    const result = await safeFetchResult(
      `${API_BASE}/appointments/monthly-forecast?clinicId=${clinicId}&date=${date}`,
      null
    )

    if (result.ok) {
      clearFeatureLocks(["advancedReports"])
      setMonthlyForecast(result.data || null)
      return
    }

    if (registerFeatureLock(result.error)) {
      setMonthlyForecast(null)
      return
    }

    setMonthlyForecast(null)
  }, [
    clinicId,
    date,
    canLoadSingleClinicAdvancedReports,
    clearFeatureLocks,
    registerFeatureLock
  ])

  useEffect(() => {
    if (!clinicId || !userId) return

    const run = async () => {
      setLoading(true)
      setFeatureLocks(UI_FEATURE_LOCK_TEMPLATE)

      await Promise.all([
        loadBundle(),
        loadFinancialAlerts(),
        loadExecutiveActions(),
        loadExecutiveScore(),
        loadHeatmap(),
        loadSaturation(),
        loadDoctorRanking(),
        loadTreatmentRanking(),
        loadWeeklyForecast(),
        loadMonthlyForecast()
      ])

      setLoading(false)
    }

    run()
  }, [
    clinicId,
    date,
    userId,
    loadBundle,
    loadFinancialAlerts,
    loadExecutiveActions,
    loadExecutiveScore,
    loadHeatmap,
    loadSaturation,
    loadDoctorRanking,
    loadTreatmentRanking,
    loadWeeklyForecast,
    loadMonthlyForecast
  ])

  return {
    loading,
    bundle,
    heatmap,
    saturation,
    doctorRanking,
    treatmentRanking,
    weeklyForecast,
    monthlyForecast,
    financialAlerts,
    executiveActions,
    executiveScore,
    featureLocks
  }
}